import { Loader2, Search, Plus } from "lucide-react";
import {
  useCallback, useEffect, useRef, useState,
} from "react";
import BaseDrawer from "@/shared/components/Drawer";
import { Button } from "@/shared/ui/Button";
import { Input } from "@/shared/ui/Input";
import { Switch } from "@/shared/ui/Switch";
import { accountingService } from "../../services/accounting.service";
import { formatAmount } from "./utils";
import type { EcommerceOrderSearchResult } from "../../types/accounting.types";

type EcommerceSearchDrawerProps = {
  open: boolean
  onClose: () => void
  isBusy: boolean
  onSelectOperation: (activityOperationId: number, operationData: {
    debit: number | null
    credit: number | null
    allocationBalance: number | null
    label: string
  }) => void
};

const EcommerceSearchDrawer = ({
  open,
  onClose,
  isBusy,
  onSelectOperation,
}: EcommerceSearchDrawerProps) => {
  const [search, setSearch] = useState("");
  const [orders, setOrders] = useState<EcommerceOrderSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [includeSettled, setIncludeSettled] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const lastSearchRef = useRef("");

  // Reset state when drawer closes
  useEffect(() => {
    if (!open) {
      setSearch("");
      setOrders([]);
      setIncludeSettled(false);
      lastSearchRef.current = "";
    }
  }, [open]);

  // Focus search input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open]);

  const runSearch = useCallback(async (query: string, settled: boolean) => {
    if (!query.trim()) {
      setOrders([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const results = await accountingService.searchEcommerceOrders(query.trim(), settled);
      setOrders(results);
    }
    catch {
      setOrders([]);
    }
    finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    lastSearchRef.current = value;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim()) {
      setOrders([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    debounceRef.current = setTimeout(() => {
      void runSearch(value, includeSettled);
    }, 300);
  }, [includeSettled, runSearch]);

  // Reload when switch changes
  const handleToggleIncludeSettled = useCallback((checked: boolean) => {
    setIncludeSettled(checked);
    if (lastSearchRef.current.trim()) {
      void runSearch(lastSearchRef.current, checked);
    }
  }, [runSearch]);

  return (
    <BaseDrawer
      drawerId="DRW-ECOM-SEARCH"
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      isFirstLevel={false}
      onBack={onClose}
      width="893px"
      zIndex={65}
      bodyClassName="[&_*]:select-text"
      title={'🛒 Affectation commande "Bons plans"'}
      description="Recherchez une commande par référence ou nom du client."
    >
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            ref={searchInputRef}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Rechercher (ref commande, nom client)..."
            className="pl-9 h-9 text-[13px]"
          />
          {isSearching && (
            <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
          )}
        </div>

        <label className="flex items-center gap-2 cursor-pointer select-none">
          <Switch
            checked={includeSettled}
            onCheckedChange={handleToggleIncludeSettled}
          />
          <span className="text-[12px] text-slate-600">Inclure les opérations soldées</span>
        </label>

        {!isSearching && search.trim().length >= 2 && orders.length === 0 && (
          <div className="text-center py-6 text-[12px] text-slate-500">
            Aucune commande trouvée.
          </div>
        )}

        {!search.trim() && (
          <div className="text-center py-8 text-[12px] text-slate-400">
            Saisissez une référence de commande ou un nom de client.
          </div>
        )}

        {!isSearching && orders.map((order) => (
          <div key={order.id} className="rounded-md border border-blue-200 bg-blue-50/60 p-3 space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <div className="text-[12px] font-semibold text-blue-900">
                {order.ref}
              </div>
              <div className="flex items-center gap-2">
                {order.statusName && (
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-blue-200/70 text-blue-900">
                    {order.statusName}
                  </span>
                )}
              </div>
            </div>
            {(order.clientName || order.clientSite) && (
              <div className="text-[11px] text-blue-700">
                {[order.clientName, order.clientSite].filter(Boolean).join(" — ")}
              </div>
            )}
            <div className="text-[11px] text-blue-700">
              Total :
              {" "}
              {formatAmount(order.total ?? 0)}
              {" "}
              € — Solde :
              {" "}
              {formatAmount(order.allocationBalance ?? 0)}
              {" "}
              €
            </div>

            <div className="space-y-1">
              {order.operations.map((op) => (
                <div
                  key={op.id}
                  className="flex items-center gap-2 rounded border border-blue-100 bg-white px-2.5 py-2"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] text-slate-700 truncate" title={op.label}>
                      {op.label || `Opération #${op.id}`}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {op.category && (
                        <>
                          {op.category}
                          {" — "}
                        </>
                      )}
                      {op.debit ? `Débit: ${formatAmount(op.debit)} €` : ""}
                      {op.credit ? `Crédit: ${formatAmount(op.credit)} €` : ""}
                      {" — "}
                      Solde à affecter :
                      {" "}
                      {formatAmount(op.allocationBalance ?? 0)}
                      {" "}
                      €
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="success"
                    size="sm"
                    className="h-7 px-2.5 text-[11px] shrink-0"
                    disabled={isBusy}
                    onClick={() => onSelectOperation(op.id, {
                      debit: op.debit,
                      credit: op.credit,
                      allocationBalance: op.allocationBalance,
                      label: op.label,
                    })}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Affecter
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </BaseDrawer>
  );
};

export default EcommerceSearchDrawer;
